import * as React from "react";

import { Generic } from "src/@types/Category";
import {
  LOCAL_ACTIVE_GRADE,
  LOCAL_ACTIVE_SCHOOL,
} from "src/constants/StorageKeys";

import agent from "src/helpers/apiAgent";

import useAuth from "src/hooks/useAuth";

// Set defaults for reuse
const DEFAULTS = {
  activeCourseId: "",
  loaders: {
    grades: false,
    courses: false,
    categories: false,
  },
  activeGrade: { id: "", name: "" },
  activeSchool: { id: "", name: "" },
  profileGrades: [],
  courses: [],
  categories: {},
  setActiveCourseId: (courseId: string) => {},
  setRefetch: (val: Generic) => {},
  getCategories: () => {},
  updateActiveGrade: (grade) => {},
  updateActiveSchool: (school) => {},
  addNewlyCreatedCategory: (category, courseId) => {},
  updateCategoryLocally: (category, courseId, categoryId) => {},
};
const CourseContext = React.createContext(DEFAULTS);

const CourseProvider: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
}) => {
  const { isLoggedIn, user } = useAuth();

  const [refetch, setRefetch] = React.useState(false);
  const [loaders, setLoaders] = React.useState(DEFAULTS.loaders);
  const [activeGrade, setActiveGrade] = React.useState(DEFAULTS.activeGrade);
  const [activeSchool, setActiveSchool] = React.useState(DEFAULTS.activeSchool);
  const [profileGrades, setProfileGrades] = React.useState(
    DEFAULTS.profileGrades
  );
  const [activeCourseId, setActiveCourseId] = React.useState(
    DEFAULTS.activeCourseId
  );
  const [courses, setCourses] = React.useState(DEFAULTS.courses);
  const [categories, setCategories] = React.useState(DEFAULTS.categories);

  // Setting previously selected school
  React.useEffect(() => {
    if (!isLoggedIn || !user?.schools?.length) return;

    const storedSchool = localStorage.getItem(LOCAL_ACTIVE_SCHOOL);
    let previouslyActiveSchool: Generic | null = null;

    if (storedSchool) {
      try {
        previouslyActiveSchool = JSON.parse(storedSchool) as Generic;
      } catch (error) {
        previouslyActiveSchool = null;
      }
    }

    let schoolToSet = user?.schools?.[0];
    if (
      previouslyActiveSchool?.id &&
      user?.schools?.find((s) => s?.id === previouslyActiveSchool?.id)
    ) {
      schoolToSet = previouslyActiveSchool;
    }
    // const schoolToSet = previouslyActiveSchool ?? user?.schools?.[0];

    // if (!activeSchool?.id && schoolToSet?.id) {
    if (schoolToSet?.id) {
      setActiveSchool(schoolToSet);
    }
  }, [user, isLoggedIn]);

  React.useEffect(() => {
    const getGrades = async () => {
      try {
        setLoaders((prev) => ({ ...prev, grades: true }));

        const response = await agent.Auth.profileGrades();

        if (response.code === 200) {
          setProfileGrades(response?.data);

          // Setting previously selected grade
          const previouslyActiveGrade: string | Generic =
            localStorage.getItem(LOCAL_ACTIVE_GRADE);

          const parsedGrade =
            previouslyActiveGrade !== null
              ? (JSON.parse(previouslyActiveGrade) as Generic)
              : null;

          const matchingGrade = response?.data?.find(
            (g) => g?.schoolId === activeSchool?.id
          );

          if (parsedGrade && parsedGrade.schoolId === activeSchool?.id) {
            setActiveGrade(parsedGrade);
          } else {
            setActiveGrade(matchingGrade ?? null);
          }
        }
      } catch (error) {
      } finally {
        setLoaders((prev) => ({ ...prev, grades: false }));
      }
    };
    if (isLoggedIn && activeSchool?.id) {
      getGrades();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, activeSchool]);

  React.useEffect(() => {
    const getCourses = async () => {
      try {
        setLoaders((prev) => ({ ...prev, courses: true }));
        const response = await agent.Auth.profileCoursesByGradeId(
          activeGrade?.id
        );

        if (response.code === 200) {
          setCourses(response?.data);
        }
      } catch (error) {
      } finally {
        setLoaders((prev) => ({ ...prev, courses: false }));
      }
    };
    if (isLoggedIn && activeGrade?.id && Object?.keys(activeGrade)?.length) {
      getCourses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, activeGrade, refetch]);

  const getCategories = React.useCallback(async () => {
    try {
      setLoaders((prev) => ({ ...prev, categories: true }));
      const promises = courses.map((course) =>
        agent.Course.getCategoriesInCourse(course?.id)
      );
      const response = await Promise.all(promises);
      const _categories = {};
      if (response.some((res) => res?.code === 200)) {
        response?.forEach((course, idx) => {
          // Replacing parent id null with 0
          course?.data?.categories?.forEach((cat) => {
            if (!cat.parent) {
              cat.parent = 0;
            }
          });

          // creating categories with course id
          _categories[courses[idx]?.id] = {
            categories: course?.data?.categories,
          };
        });
        setCategories(_categories);
      }
    } catch (error) {
    } finally {
      setLoaders((prev) => ({ ...prev, categories: false }));
    }
  }, [courses]);

  const addNewlyCreatedCategory = (category, courseId) => {
    if (courseId) {
      const cat = {
        author: category?.author,
        droppable: true,
        id: category?.id,
        logs: [],
        parent: category?.parentId,
        text: category?.name,
      };

      const _categories = JSON.parse(JSON.stringify(categories));
      if (!_categories[courseId]?.categories?.length) {
        _categories[courseId].categories = [];
      }
      _categories[courseId].categories.push(cat);
      setCategories(_categories);
    }
  };

  const updateCategoryLocally = (category, courseId, categoryId) => {
    if (courseId) {
      const cat = {
        author: category?.author,
        droppable: true,
        id: category?.id,
        logs: category?.logs,
        parent: category?.parentId,
        text: category?.name,
      };

      const _categories = JSON.parse(JSON.stringify(categories));
      if (!_categories[courseId]?.categories?.length) {
        _categories[courseId].categories = [];
      }

      const index = _categories[courseId]?.categories.findIndex(
        (cat) => cat.id === categoryId
      );

      if (index !== -1) {
        _categories[courseId].categories[index] = { ...cat };
      }

      setCategories(_categories);
    }
  };

  React.useEffect(() => {
    if (isLoggedIn && courses?.length) {
      getCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses]);

  const updateActiveGrade = (grade) => {
    setActiveGrade(grade);
    localStorage.setItem(LOCAL_ACTIVE_GRADE, JSON.stringify(grade));
  };

  const updateActiveSchool = (school) => {
    setActiveSchool(school);
    localStorage.setItem(LOCAL_ACTIVE_SCHOOL, JSON.stringify(school));
  };

  const contextValues = {
    activeCourseId,
    activeGrade,
    activeSchool,
    profileGrades,
    courses,
    loaders,
    setRefetch,
    categories,
    getCategories,
    setActiveCourseId,
    updateActiveGrade,
    updateActiveSchool,
    updateCategoryLocally,
    addNewlyCreatedCategory,
  };

  return (
    <CourseContext.Provider value={contextValues}>
      {children}
    </CourseContext.Provider>
  );
};

export { CourseContext };
export default CourseProvider;
